// src/components/AIAssistantManagement.js

import React, { useState, useEffect, useRef , useCallback} from 'react';
import axios from 'axios';
import './AIAssistantManagement.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaRobot, FaPen, FaCode, FaTrash, FaPlay, FaPlus, FaTools, FaCheck, FaCommentDots, FaTimes, FaArrowLeft, FaRegCopy } from 'react-icons/fa';
import Button from './UI/Button';
import ChatbotTest from './ChatbotTest';
import AssistantVersions from './AssistantVersions';
import AssistantRules from './AssistantRules';

const AIAssistantManagement = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState(undefined);
  const [aiAssistants, setAIAssistants] = useState([]);
  const [selectedAssistant, setSelectedAssistant] = useState(null);
  // Monotonic id of the latest detail fetch, so a slow response for a
  // previously clicked assistant can never overwrite the current selection.
  const assistantDetailRequestRef = useRef(0);
  const [name, setName] = useState('');
  const [prompt, setPrompt] = useState('');
  const [questions, setQuestions] = useState(['', '', '', '']);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showPopup, setShowPopup] = useState(false);
  const [promptPopupOpen, setPromptPopupOpen] = useState(false);
  const [promptInput, setPromptInput] = useState('');
  const [promptMode, setPromptMode] = useState('edit');
  const [availableTools, setAvailableTools] = useState([]);
  const [toolPolicies, setToolPolicies] = useState({});      // tool -> mode (tenant override), backend plan H2
  const [policyTools, setPolicyTools] = useState({});        // tool -> {effect, default_policy, human_allowed}
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const navigate = useNavigate();
  const [automation, setAutomation] = useState(false);
  const [showAgentSelector, setShowAgentSelector] = useState(false);
  const [showCreateAgentModal, setShowCreateAgentModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedPrompt, setEditedPrompt] = useState('');
  const [editedToolIds, setEditedToolIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showToolsOverlay, setShowToolsOverlay] = useState(false);
  // Tool picker state lives here: the overlay is re-created on every render
  // of this component, so state declared inside it would reset on each toggle.
  const [toolsQuery, setToolsQuery] = useState('');
  const [toolsOpenGroups, setToolsOpenGroups] = useState({});
  const [showChatBubble, setShowChatBubble] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);
  const chatbotTestRef = useRef(null);

  // Removed activeTab state - pages are now separate routes

  const questionTitles = [
    t('aiAssistant.question1'),
    t('aiAssistant.question2'),
    t('aiAssistant.question3'),
    t('aiAssistant.question4'),
  ];

  // --- Dummy Data for "experience" role ---
  const dummyAIAssistantsData = [
    { _id: 'exp_asst_1', name: 'Customer Service Pro (Demo)', prompt: 'You are a friendly and efficient customer service AI. Help users with their inquiries about orders, returns, and product information. Prioritize quick and accurate solutions.', selected_tool_ids: ['exp_tool_1', 'exp_tool_2'], is_running: true, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { _id: 'exp_asst_2', name: 'Technical Support Bot (Demo)', prompt: 'Assist users with technical issues, troubleshooting, and setup guides. Be patient and provide clear, step-by-step instructions.', selected_tool_ids: ['exp_tool_3'], is_running: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
    { _id: 'exp_asst_3', name: 'Sales Advisor (Demo)', prompt: 'Guide customers through our product catalog, offer recommendations, and help them complete purchases. Focus on upselling and cross-selling opportunities.', selected_tool_ids: [], is_running: false, created_at: new Date().toISOString(), updated_at: new Date().toISOString() },
  ];

  const dummyAvailableToolsData = [
    { id: 'exp_tool_1', name: 'get_tracking_info', description: 'Retrieves tracking information for an order.' },
    { id: 'exp_tool_2', name: 'Retrieve_customer_order_by_ID', description: 'Fetches order details using the order ID.' },
    { id: 'exp_tool_3', name: 'retrieve_knowledge', description: 'Searches the knowledge base for answers.' },
    { id: 'exp_tool_4', name: 'assign_to_employee', description: 'Assigns the ticket to a human agent.' },
  ];

  // --- End of Dummy Data ---

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/');
      return;
    }
    setIsLoading(true); // Set loading true at the beginning
    fetchUserData(token);
    
    const urlParams = new URLSearchParams(window.location.search);
    const assistantIdFromUrl = urlParams.get('assistantId');
    if (assistantIdFromUrl) {
      localStorage.setItem('pendingAssistantSelection', assistantIdFromUrl);
    }
  }, [navigate]);

  useEffect(() => {
    if (user === null) { // If fetchUserData explicitly sets user to null (error)
      navigate('/');
    } else if (user) { // User data is fetched
      if (user.role === 'experience') {
        loadExperienceModeData();
      } else {
        const token = localStorage.getItem('access_token');
        fetchData(token); // Fetch real data for other roles
      }
      // Set automation status based on user profile or default for experience
      setAutomation(user.role === 'experience' ? true : (user.automation || false));
    }
  }, [user]);



  const loadExperienceModeData = () => {
    setAIAssistants(dummyAIAssistantsData);
    setAvailableTools(dummyAvailableToolsData);

    const pendingAssistantId = localStorage.getItem('pendingAssistantSelection');
    let assistantToSelect = null;

    if (pendingAssistantId) {
      assistantToSelect = dummyAIAssistantsData.find(a => a._id === pendingAssistantId);
      localStorage.removeItem('pendingAssistantSelection');
    }
    
    if (!assistantToSelect) {
      assistantToSelect = dummyAIAssistantsData.find(a => a.is_running) || dummyAIAssistantsData[0];
    }

    if (assistantToSelect) {
      setSelectedAssistant(assistantToSelect);
    }
    setIsLoading(false);
  };


  useEffect(() => {
    if (selectedAssistant) {
      setName(selectedAssistant.name);
      setPrompt(selectedAssistant.prompt);
      setSelectedToolIds(selectedAssistant.selected_tool_ids || []);
      setEditedName(selectedAssistant.name);
      setEditedPrompt(selectedAssistant.prompt);
      setEditedToolIds(selectedAssistant.selected_tool_ids || []);
    } else {
      // If no assistant is selected (e.g. after deleting the selected one in experience mode)
      // reset relevant form fields or select another default if applicable.
       if (user && user.role === 'experience' && aiAssistants.length > 0) {
         const defaultAssistant = aiAssistants.find(a => a.is_running) || aiAssistants[0];
         if(defaultAssistant) setSelectedAssistant(defaultAssistant);
         else resetForm(); // Or handle no assistants case
       } else if (!user || user.role !== 'experience') {
          // For non-experience users, if selectedAssistant becomes null, reset or handle as per existing logic
          // This part might need adjustment based on how non-experience users expect it to behave when selectedAssistant is null
       }
    }
  }, [selectedAssistant, user, aiAssistants]); // Added user and aiAssistants to dependencies

  const fetchUserData = async (token) => {
    try {
      const response = await axios.get('/api/users/user', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      setUser(null); // Set user to null on error to trigger redirect or stop loading
      setIsLoading(false);
    }
  };

  const fetchData = async (token, preferredAssistantId = null) => {
    // This function is now only for non-experience users
    if (user && user.role === 'experience') {
      setIsLoading(false); // Should already be handled by loadExperienceModeData
      return;
    }
    try {
      // setIsLoading(true) is managed by the calling useEffect or initial state
      const [assistantsResponse, toolsResponse] = await Promise.all([
        axios.get('/api/ai-assistants', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get('/api/tools', {
          headers: { Authorization: `Bearer ${token}` },
        })
      ]);

      const assistants = assistantsResponse.data;
      setAIAssistants(assistants);
      setAvailableTools(toolsResponse.data);
      setIsLoading(false);

      // The list endpoint only carries summary fields (no improvement
      // history, and possibly no prompt in the future): pick which assistant
      // to show here, then hydrate it through GET /api/ai-assistants/{id}.
      const pendingAssistantId = localStorage.getItem('pendingAssistantSelection');
      let assistantIdToSelect = null;
      if (pendingAssistantId) {
        assistantIdToSelect = pendingAssistantId;
        localStorage.removeItem('pendingAssistantSelection');
      } else if (preferredAssistantId && assistants.some(a => a._id === preferredAssistantId)) {
        assistantIdToSelect = preferredAssistantId;
      } else {
        const runningAgent = assistants.find(assistant => assistant.is_running);
        assistantIdToSelect = runningAgent ? runningAgent._id : (assistants[0]?._id || null);
      }

      if (assistantIdToSelect) {
        await selectAssistantById(assistantIdToSelect, token, assistants);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setIsLoading(false);
    }
  };

  const fetchAssistantDetail = async (assistantId, token) => {
    const response = await axios.get(`/api/ai-assistants/${assistantId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  };

  // Select an assistant by id: show the list summary right away (if we have
  // one) and replace it with the full document from the detail endpoint.
  const selectAssistantById = async (assistantId, token, assistantsList = aiAssistants) => {
    const summary = assistantsList.find(a => a._id === assistantId) || null;
    if (summary) {
      setSelectedAssistant(summary);
    }
    const requestId = ++assistantDetailRequestRef.current;
    try {
      const detail = await fetchAssistantDetail(assistantId, token || localStorage.getItem('access_token'));
      if (requestId !== assistantDetailRequestRef.current) return; // superseded by a newer selection
      if (detail && detail._id) {
        setSelectedAssistant(detail);
      }
    } catch (error) {
      console.error('Failed to fetch AI assistant details:', error);
      // Keep the summary (if any) so the page stays usable.
    }
  };


  const handleDeleteAIAssistant = async (assistantIdToDelete) => {
    if (!assistantIdToDelete) {
      console.error('Assistant ID is undefined');
      alert('Unable to delete AI assistant: Missing ID');
      return;
    }

    if (user && user.role === 'experience') {
      setAIAssistants(prev => prev.filter(a => a._id !== assistantIdToDelete));
      if (selectedAssistant && selectedAssistant._id === assistantIdToDelete) {
        // If the deleted assistant was selected, select another one or reset
        const remainingAssistants = aiAssistants.filter(a => a._id !== assistantIdToDelete);
        if (remainingAssistants.length > 0) {
            setSelectedAssistant(remainingAssistants.find(a => a.is_running) || remainingAssistants[0]);
        } else {
            resetForm(); // No more assistants left
        }
      }
      alert('AI assistant (Demo) deleted successfully');
      return;
    }
    
    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(`/api/ai-assistants/${assistantIdToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('AI assistant deleted successfully');
      fetchData(token); // Re-fetch real data
    } catch (error) {
      console.error('Failed to delete AI assistant:', error);
      alert('Failed to delete AI assistant.');
    }
  };

  const handleCreateAIAssistant = async () => {
    if (user && user.role === 'experience') {
      const newDummyAssistant = {
        _id: `exp_asst_${Date.now()}`,
        name: name || 'New Demo Agent',
        prompt: prompt || 'Default demo prompt.',
        selected_tool_ids: selectedToolIds || [],
        is_running: false, // New agents aren't running by default
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      setAIAssistants(prev => [...prev, newDummyAssistant]);
      setSelectedAssistant(newDummyAssistant); // Select the newly created dummy assistant
      resetForm(); // Reset form fields for next creation, but name/prompt/tools are from current state
      setShowCreateAgentModal(false); // Close modal after creation
      alert('AI assistant (Demo) created successfully');
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        '/api/ai-assistants',
        { name, prompt, selected_tool_ids: selectedToolIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedAssistant(response.data); // Select the newly created assistant
      fetchData(token, response.data?._id); // Re-fetch real data, keep the new one selected
      resetForm();
      setShowCreateAgentModal(false); // Close modal
      alert('AI assistant created successfully');
    } catch (error) {
      if (error.response && error.response.status === 400) {
        console.error('Failed to create AI assistant:', error.response.data.detail);
        alert(error.response.data.detail);
      } else {
        console.error('Failed to create AI assistant:', error);
        alert('Failed to create AI assistant.');
      }
    }
  };

  const handleUpdateAIAssistant = async () => {
    if (!selectedAssistant) {
      alert(t('aiAssistant.noAgentSelectedUpdate'));
      return;
    }
    if (user && user.role === 'experience') {
      const updatedAssistants = aiAssistants.map(a =>
        a._id === selectedAssistant._id
          ? { ...a, name: editedName, prompt: editedPrompt, selected_tool_ids: editedToolIds, updated_at: new Date().toISOString() }
          : a
      );
      setAIAssistants(updatedAssistants);
      const newlyUpdatedAssistant = updatedAssistants.find(a => a._id === selectedAssistant._id);
      setSelectedAssistant(newlyUpdatedAssistant); // Update selected assistant with new details
      setName(editedName); // Sync top-level state if needed, though selectedAssistant change should trigger it
      setPrompt(editedPrompt);
      setSelectedToolIds(editedToolIds);
      setIsEditing(false);
      alert(t('aiAssistant.updateSuccessDemo'));
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/ai-assistants/${selectedAssistant._id}`,
        { 
          name: editedName,
          prompt: editedPrompt,
          selected_tool_ids: editedToolIds 
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh the list and re-hydrate the edited assistant from the detail endpoint
      fetchData(token, selectedAssistant._id);
      setIsEditing(false);
      alert(t('aiAssistant.updateSuccess'));
    } catch (error) {
      console.error('Failed to update AI assistant:', error);
      alert(t('aiAssistant.updateError'));
    }
  };

  const handleSelectAssistant = (assistant) => {
    setIsEditing(false); // Exit editing mode when switching assistants
    if (!assistant) return;
    if ((user && user.role === 'experience') || !assistant._id) {
      setSelectedAssistant(assistant); // Demo data lives only in memory
      return;
    }
    // Real data: the list is a summary, load the full document on demand.
    // Form fields will update via the useEffect hook that depends on selectedAssistant.
    selectAssistantById(assistant._id, localStorage.getItem('access_token'));
  };

  const handleSetRunning = async (assistantIdToRun) => {
    if (user && user.role === 'experience') {
      const updatedAssistants = aiAssistants.map(a => ({
        ...a,
        is_running: a._id === assistantIdToRun,
      }));
      setAIAssistants(updatedAssistants);
      // If the currently selected assistant's running status changed, update it
      if (selectedAssistant && selectedAssistant._id === assistantIdToRun) {
        setSelectedAssistant(prev => ({...prev, is_running: true}));
      } else if (selectedAssistant && selectedAssistant.is_running && selectedAssistant._id !== assistantIdToRun){
         setSelectedAssistant(prev => ({...prev, is_running: false}));
      }
      // Update the selected assistant if it's the one being run
      const newlyRunAssistant = updatedAssistants.find(a => a._id === assistantIdToRun);
      if (newlyRunAssistant && selectedAssistant && selectedAssistant._id === assistantIdToRun) {
        // setSelectedAssistant(newlyRunAssistant); // This might cause loop if selectedAssistant effect is not careful
      } else if (newlyRunAssistant) {
        // If a different assistant is made running, the currently selected one might need its is_running flag updated in state
        const currentSelected = updatedAssistants.find(a => a._id === (selectedAssistant ? selectedAssistant._id : null));
        if (currentSelected) setSelectedAssistant(currentSelected);
      }
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        `/api/ai-assistants/${assistantIdToRun}/running`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData(token); // Re-fetch to get updated running status
    } catch (error) {
      console.error('Failed to set AI assistant as running:', error);
      alert('Failed to set AI assistant as running.');
    }
  };
  
  const resetForm = () => {
    // When resetting form for creation or after deletion
    // setSelectedAssistant(null); // This might be too disruptive if user wants to create multiple
    setName('');
    setPrompt('');
    setSelectedToolIds([]);
    setPromptInput(''); // If this is for prompt generation popup
    
    // For editing state, ensure these are also reset or handled
    setEditedName('');
    setEditedPrompt('');
    setEditedToolIds([]);
    setIsEditing(false);
  };


  const handleOpenPopup = () => {
    setShowPopup(true);
    setCurrentQuestionIndex(0);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
    setCurrentQuestionIndex(0);
  };

  const handleQuestionChange = (index, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = value;
    setQuestions(updatedQuestions);
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePromptPopupOpen = () => {
    setPromptInput(prompt);
    setPromptMode('edit');
    setPromptPopupOpen(true);
  };

  const handlePromptPopupClose = () => {
    setPromptPopupOpen(false);
    setPromptMode('edit');
  };

  const handlePromptSave = () => {
    setPrompt(promptInput);
    handlePromptPopupClose();
  };

  const handleSwitchToGenerate = () => {
    setPromptMode('generate');
    setQuestions(['', '', '', '']);
  };

  const handleGenerateInstructions = async () => {
    if (user && user.role === 'experience') {
      // Simulate instruction generation
      const generatedPrompt = `Based on your answers (Q1: ${questions[0]}, Q2: ${questions[1]}, etc.), here are your generated demo instructions for the AI. This is a placeholder.`;
      setPromptInput(generatedPrompt);
      setPromptMode('edit'); // Switch to edit mode to show the generated prompt
      alert('Demo instructions generated!');
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        '/api/ai-assistants/generate-instructions',
        { questions },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      setPromptInput(response.data.instructions);
      setPromptMode('edit');
    } catch (error) {
      console.error('Failed to generate instructions:', error);
      alert('Failed to generate instructions.');
    }
  };

  const handlePromptChange = (e) => {
    setPromptInput(e.target.value);
  };

  const getPromptPreview = (text) => {
    const maxLength = 50;
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };


  const handleAutomationToggle = async () => {
    const newAutomationState = !automation;
    if (user && user.role === 'experience') {
      setAutomation(newAutomationState);
      // Optionally update the dummy user object if it were in state, but here just local state
      alert(`Demo automation ${newAutomationState ? 'enabled' : 'disabled'}.`);
      return;
    }
    try {
      const token = localStorage.getItem('access_token');
      await axios.put(
        '/api/users/automation',
        { automation: newAutomationState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAutomation(newAutomationState);
    } catch (error) {
      console.error('Failed to toggle automation:', error);
      alert('Failed to toggle automation.');
    }
  };

  const handleAgentCircleClick = () => {
    setShowAgentSelector(true);
  };

  // Registry metadata (backend plan H1): what the tool does to the world and
  // whether it runs at once, needs a validation, or is refused.
  const loadToolPolicies = useCallback(async () => {
    try {
      const response = await axios.get('/api/ai-proposals/policies', {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      const modes = {};
      (response.data.policies || []).forEach((p) => { modes[p.tool] = p.mode; });
      const tools = {};
      (response.data.tools || []).forEach((tl) => { tools[tl.name] = tl; });
      setToolPolicies(modes);
      setPolicyTools(tools);
    } catch (e) {
      // The policy editor is optional: a failed load keeps the registry defaults.
    }
  }, []);

  useEffect(() => { loadToolPolicies(); }, [loadToolPolicies]);

  const setToolPolicy = async (toolName, mode) => {
    try {
      await axios.put(`/api/ai-proposals/policies/${encodeURIComponent(toolName)}`, { mode: mode || null, conditions: {} }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setToolPolicies((prev) => {
        const next = { ...prev };
        if (mode) next[toolName] = mode; else delete next[toolName];
        return next;
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : t('tools.policySaveError', 'Impossible d’enregistrer la politique de cet outil.'));
    }
  };

  // The tenant's execution policy of an effectful tool (backend plan H2).
  const toolPolicySelect = (tool) => {
    const name = (tool.function && tool.function.name) || tool.name;
    const meta = policyTools[name];
    if (!meta) return null;
    const current = toolPolicies[name] || '';
    return (
      <select
        className="tool-policy-select"
        value={current}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setToolPolicy(name, e.target.value)}
        title={t('tools.policyHint', 'Quand l’IA appelle cet outil : exécuter, demander votre validation, ou refuser.')}
      >
        <option value="">{t('tools.policyDefault', 'Par défaut ({{mode}})', { mode: t(`tools.policyMode.${meta.default_policy}`, meta.default_policy) })}</option>
        <option value="auto">{t('tools.policyMode.auto', 'automatique')}</option>
        {meta.human_allowed && <option value="human">{t('tools.policyMode.human', 'validation humaine')}</option>}
        <option value="forbidden">{t('tools.policyMode.forbidden', 'interdit')}</option>
      </select>
    );
  };

  const toolEffectBadge = (tool) => {
    if (!tool || !tool.effect) return null;
    const labels = {
      read: t('tools.effect.read', 'Read'),
      propose: t('tools.effect.propose', 'Proposal'),
      commit: t('tools.effect.commit', 'Action'),
    };
    const policy = tool.policy === 'proposal'
      ? ` · ${t('tools.policy.proposal', 'validation')}`
      : (tool.policy === 'forbidden' ? ` · ${t('tools.policy.forbidden', 'forbidden')}` : '');
    const title = [tool.source, tool.policy, tool.cost].filter(Boolean).join(' · ');
    return (
      <>
        <span className={`tool-effect-badge effect-${tool.effect}`} title={title}>
          {labels[tool.effect] || tool.effect}{policy}
        </span>
        {tool.registered === false && (
          <span className="tool-effect-badge effect-unregistered" title={t('tools.unregisteredHint', 'This tool has no executor: it is not offered to the assistant.')}>
            {t('tools.unregistered', 'not executable')}
          </span>
        )}
        {tool.deprecated_by && (
          <span className="tool-effect-badge effect-deprecated" title={t('tools.deprecatedBy', 'Replaced by {{name}}', { name: tool.deprecated_by })}>
            {t('tools.deprecated', 'legacy')}
          </span>
        )}
      </>
    );
  };

  const getToolTranslation = (toolName) => {
    const toolTranslations = {
      'retrieve_knowledge': t('tools.retrieveKnowledge'),
      'assign_to_employee': t('tools.assignToEmployee'),
      'resolve_ticket': t('tools.resolveTicket'),
      'Close_ticket': t('tools.closeTicket'),
      'get_tracking_info': t('tools.getTrackingInfo'),
      'Retrieve_customer_order_by_reference_and_email': t('tools.retrieveOrder'),
      'Update_customer_shipping_address': t('tools.updateShippingAddress'),
      'find_relay_points': t('tools.findRelayPoints'),
      'create_return_with_edn_label': t('tools.createReturn'),
      'carriers_conversation': t('tools.carriersConversation'),
      'Retour': t('tools.return'),
      'fill_form_fields': t('tools.fillPDF'),
      'getOrderById': t('tools.getOrderById'),
      'carriers_conversation_update': t('tools.carriersConversationUpdate'),
      'order_status': t('tools.orderStatus'),
      'Request_return_order': t('tools.Request_return_order'),
      'Retrieve_customer_order_by_ID': t('tools.Retrieve_customer_order_by_ID'),
      'Request_exchange_shopify': t('tools.Request_exchange_shopify'),
      'Get_all_product_variants': t('tools.Get_all_product_variants')
    };
    return toolTranslations[toolName] || toolName;
  };

  const handleStartEditing = () => {
    setIsEditing(true);
    setEditedName(name);
    setEditedPrompt(prompt);
    setEditedToolIds(selectedToolIds);
  };

  const handleCancelEditing = () => {
    setIsEditing(false);
    setEditedName(name);
    setEditedPrompt(prompt);
    setEditedToolIds(selectedToolIds);
  };

  const handlePromptClick = () => {
    setShowPromptModal(true);
  };

  const handleClosePromptModal = () => {
    setShowPromptModal(false);
  };

  const handleChatBubbleToggle = () => {
    setShowChatBubble(!showChatBubble);
  };

  const handleDeleteConversation = async () => {
    if (!user) return;
    
    try {
      // Call the delete function from the ChatbotTest component
      if (chatbotTestRef.current) {
        await chatbotTestRef.current.deleteConversation();
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  // --- Tool picker -----------------------------------------------------------
  // The tools are grouped by what they do so an operator can read them instead
  // of decoding truncated names: the two carrier-contact tool sets first (the
  // agent chooses the channel / the configuration decides), then ticket,
  // tracking, orders, the tenant's own integrations, and the legacy tools.
  const AGENT_CARRIER_TOOL_RE = /^(send_carrier_email|file_[a-z0-9_]+_request|file_fedex_claim)$/;
  const ROUTER_CARRIER_TOOLS = ['contact_carrier', 'update_carrier_conversation'];
  const TICKET_TOOLS = [
    'assign_to_employee', 'resolve_ticket', 'Close_ticket', 'change_ticket_status', 'pass_response',
    'attach_ticket_files', 'send_invoice', 'send_proof_of_delivery', 'retrieve_knowledge', 'send_slack_message',
    'warehouse_conversation_mail',
  ];
  const TOOL_GROUP_ORDER = ['agentCarrier', 'routerCarrier', 'ticket', 'tracking', 'orders', 'integrations', 'other', 'legacy'];

  const toolFunctionName = (tool) => (tool.function && tool.function.name) || tool.name || '';

  const toolGroupOf = (tool) => {
    const name = toolFunctionName(tool);
    if (tool.deprecated_by) return 'legacy';
    if (AGENT_CARRIER_TOOL_RE.test(name)) return 'agentCarrier';
    if (ROUTER_CARRIER_TOOLS.includes(name)) return 'routerCarrier';
    if (['lead_form', 'pdf', 'openapi', 'connector'].includes(tool.source)) return 'integrations';
    if (TICKET_TOOLS.includes(name)) return 'ticket';
    if (/order|product|shopify|discount|return|exchange|sku|variant|stock|cart/i.test(name)) return 'orders';
    if (/tracking|proof|relay|delivery|parts|asus|shipment|compat|carrier|warehouse/i.test(name)) return 'tracking';
    return 'other';
  };

  const toolGroupMeta = (group) => ({
    agentCarrier: {
      title: t('tools.groups.agentCarrier', 'Contact transporteur — l’agent choisit le canal'),
      hint: t('tools.groups.agentCarrierHint', 'Un outil par canal (e-mail) et par automatisation portail ou formulaire. Dès qu’un de ces outils est sélectionné, l’assistant décide lui-même du canal et de l’adresse à partir des fiches transporteurs et de vos règles ; les outils « canal décidé par la configuration » sont alors ignorés.'),
    },
    routerCarrier: {
      title: t('tools.groups.routerCarrier', 'Contact transporteur — canal décidé par la configuration'),
      hint: t('tools.groups.routerCarrierHint', 'Un seul outil : la configuration du transporteur (portail, formulaire, e-mail) choisit le canal et l’adresse à la place de l’assistant.'),
    },
    ticket: { title: t('tools.groups.ticket', 'Ticket et réponse'), hint: '' },
    tracking: { title: t('tools.groups.tracking', 'Suivi, transporteurs et entrepôt'), hint: '' },
    orders: { title: t('tools.groups.orders', 'Commandes et boutique'), hint: '' },
    integrations: { title: t('tools.groups.integrations', 'Vos intégrations (formulaires, PDF, API, connecteurs)'), hint: '' },
    other: { title: t('tools.groups.other', 'Autres outils'), hint: '' },
    legacy: {
      title: t('tools.groups.legacy', 'Anciens outils'),
      hint: t('tools.groups.legacyHint', 'Remplacés par les outils ci-dessus ; conservés pour les assistants qui les utilisent encore.'),
    },
  }[group]);

  const toolDescription = (tool) => {
    const description = tool.function && tool.function.description;
    return typeof description === 'string' ? description.trim() : '';
  };

  const ToolsOverlay = ({ show, onClose, availableTools, selectedToolIds, onToolToggle, query, setQuery, openGroups, setOpenGroups }) => {
    if (!show) return null;

    const needle = query.trim().toLowerCase();
    const matches = (tool) => {
      if (!needle) return true;
      const name = toolFunctionName(tool);
      return [name, tool.name, getToolTranslation(name), toolDescription(tool)]
        .some((text) => String(text || '').toLowerCase().includes(needle));
    };
    const grouped = {};
    availableTools.filter(matches).forEach((tool) => {
      const group = toolGroupOf(tool);
      (grouped[group] = grouped[group] || []).push(tool);
    });
    Object.values(grouped).forEach((list) => list.sort((a, b) => toolFunctionName(a).localeCompare(toolFunctionName(b))));
    const selectedCount = availableTools.filter((tool) => selectedToolIds.includes(tool.id)).length;
    const agentSetActive = availableTools.some(
      (tool) => selectedToolIds.includes(tool.id) && AGENT_CARRIER_TOOL_RE.test(toolFunctionName(tool))
    );
    const isGroupOpen = (group) => (group === 'legacy' ? !!openGroups[group] || !!needle : openGroups[group] !== false);
    const toggleGroup = (group) => setOpenGroups((prev) => ({ ...prev, [group]: !isGroupOpen(group) }));
    const setGroup = (tools, checked) => tools.forEach((tool) => {
      if (selectedToolIds.includes(tool.id) !== checked) onToolToggle(tool.id, checked);
    });

    return (
      <div className="modalai tools-overlay">
        <div className="modal-content tools-picker">
          <div className="tools-picker-head">
            <h3>{t('aiAssistant.selectTools')}</h3>
            <span className="tools-picker-count">
              {t('tools.selectedCount', '{{count}} sélectionné(s)', { count: selectedCount })}
            </span>
          </div>
          <input
            type="search"
            className="tools-picker-search"
            placeholder={t('tools.searchPlaceholder', 'Rechercher un outil (nom ou description)…')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {agentSetActive && (
            <div className="tools-picker-notice">
              {t('tools.agentSetActive', 'Cet assistant est sur le jeu d’outils « l’agent choisit le canal » : contact_carrier et les anciens outils de conversation transporteur ne lui sont plus présentés.')}
            </div>
          )}
          <div className="tools-picker-groups">
            {TOOL_GROUP_ORDER.filter((group) => grouped[group] && grouped[group].length).map((group) => {
              const meta = toolGroupMeta(group);
              const tools = grouped[group];
              const open = isGroupOpen(group);
              const selectedInGroup = tools.filter((tool) => selectedToolIds.includes(tool.id)).length;
              return (
                <section key={group} className={`tools-picker-group tools-picker-group-${group}`}>
                  <div className="tools-picker-group-head">
                    <button type="button" className="tools-picker-group-toggle" onClick={() => toggleGroup(group)} aria-expanded={open}>
                      <span className="tools-picker-caret">{open ? '▾' : '▸'}</span>
                      <span className="tools-picker-group-title">{meta.title}</span>
                      <span className="tools-picker-group-count">{selectedInGroup}/{tools.length}</span>
                    </button>
                    <span className="tools-picker-group-actions">
                      <button type="button" onClick={() => setGroup(tools, true)}>{t('tools.selectAll', 'Tout')}</button>
                      <button type="button" onClick={() => setGroup(tools, false)}>{t('tools.selectNone', 'Aucun')}</button>
                    </span>
                  </div>
                  {open && meta.hint && <p className="tools-picker-group-hint">{meta.hint}</p>}
                  {open && (
                    <div className="tools-picker-list">
                      {tools.map((tool) => {
                        const name = toolFunctionName(tool);
                        const label = getToolTranslation(name);
                        const description = toolDescription(tool);
                        return (
                          <label key={tool.id} className={`tools-picker-item${selectedToolIds.includes(tool.id) ? ' is-selected' : ''}`} htmlFor={`tool-${tool.id}`}>
                            <input
                              type="checkbox"
                              id={`tool-${tool.id}`}
                              checked={selectedToolIds.includes(tool.id)}
                              onChange={(e) => onToolToggle(tool.id, e.target.checked)}
                            />
                            <span className="tools-picker-item-body">
                              <span className="tools-picker-item-title">
                                <span className="tools-picker-item-label">{label}</span>
                                {label !== name && <code className="tools-picker-item-name">{name}</code>}
                                {toolEffectBadge(tool)}
                              </span>
                              {description && (
                                <span className="tools-picker-item-description" title={description}>{description}</span>
                              )}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
            {!Object.keys(grouped).length && (
              <p className="tools-picker-empty">{t('tools.noMatch', 'Aucun outil ne correspond à cette recherche.')}</p>
            )}
          </div>
          <div className="modal-actions">
            <Button variant="secondary" onClick={onClose}>
              {t('aiAssistant.close')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  if (user === undefined || isLoading) {
    return (
      <div className="ai-assistant-container">
        <div className="aiam-card">
          <div className="aiam-skeleton" aria-busy="true">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="aiam-skeleton-row" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (user === null && !isLoading) { // Check !isLoading to avoid premature redirect
     // Navigation is handled in useEffect [user, navigate]
    return <div className="error-state">Error loading user data or unauthorized. Redirecting...</div>; // Fallback UI
  }

  // --- Prompt document rendering -------------------------------------------
  // Agent prompts are structured text (sections, numbered rules, **bold**,
  // `code`). Render that structure instead of dumping the raw markup.
  const renderInlineMd = (text) => {
    const parts = [];
    const regex = /(\*\*[^*]+\*\*|`[^`]+`)/g;
    let last = 0;
    let match;
    let key = 0;
    while ((match = regex.exec(text)) !== null) {
      if (match.index > last) parts.push(text.slice(last, match.index));
      const token = match[0];
      if (token.startsWith('**')) parts.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
      else parts.push(<code key={key++}>{token.slice(1, -1)}</code>);
      last = match.index + token.length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts;
  };

  const parsePromptBlocks = (raw) => {
    const blocks = [];
    (raw || '').split('\n').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) { blocks.push({ type: 'space' }); return; }
      if (/^[─—\-_=]{4,}$/.test(trimmed)) { blocks.push({ type: 'hr' }); return; }
      if (/^<\/?[A-Z_]+>$/.test(trimmed)) { blocks.push({ type: 'tag', text: trimmed }); return; }
      const heading = trimmed.match(/^(\d+)[.)]?\s+([A-Z][A-Z0-9 &'’\-/()]+)$/);
      if (heading) { blocks.push({ type: 'heading', num: heading[1], text: heading[2] }); return; }
      const rule = trimmed.match(/^(\d+\.\d+(?:\s*bis)?)\s+(.*)$/);
      if (rule) { blocks.push({ type: 'rule', num: rule[1], text: rule[2] }); return; }
      if (/^[•*-]\s+/.test(trimmed)) { blocks.push({ type: 'bullet', text: trimmed.replace(/^[•*-]\s+/, '') }); return; }
      blocks.push({ type: 'p', text: trimmed });
    });
    return blocks;
  };

  const promptBlocks = parsePromptBlocks(prompt);
  const promptSections = promptBlocks.filter((b) => b.type === 'heading');
  const promptWordCount = (prompt || '').split(/\s+/).filter(Boolean).length;

  const scrollToPromptSection = (num) => {
    const el = document.getElementById(`prompt-sec-${num}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(prompt || '');
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch {
      setPromptCopied(false);
    }
  };

  const renderPromptBlock = (block, index) => {
    switch (block.type) {
      case 'heading':
        return (
          <h3 key={index} id={`prompt-sec-${block.num}`} className="pdoc-heading">
            <span className="pdoc-heading-num">{block.num}</span>
            {block.text}
          </h3>
        );
      case 'rule':
        return (
          <p key={index} className="pdoc-rule">
            <span className="pdoc-rule-num">{block.num}</span>
            <span className="pdoc-rule-text">{renderInlineMd(block.text)}</span>
          </p>
        );
      case 'bullet':
        return (
          <p key={index} className="pdoc-bullet">
            <span className="pdoc-bullet-dot" aria-hidden="true" />
            <span className="pdoc-rule-text">{renderInlineMd(block.text)}</span>
          </p>
        );
      case 'hr':
        return <hr key={index} className="pdoc-hr" />;
      case 'tag':
        return <div key={index} className="pdoc-tag">{block.text}</div>;
      case 'space':
        return <div key={index} className="pdoc-space" />;
      default:
        return <p key={index} className="pdoc-p">{renderInlineMd(block.text)}</p>;
    }
  };

  return (
    <div className="ai-assistant-container">
      {/* Header: back + identity + actions */}
      <div className="ai-assistant-header">
        <div className="aiam-header-left">
          <button
            type="button"
            className="aiam-back-button"
            onClick={() => navigate('/my-ai-assistants')}
            aria-label={t('aiAssistant.backToAgents', 'Retour aux agents')}
          >
            <FaArrowLeft />
          </button>
          <span className="aiam-avatar"><FaRobot /></span>
          <h1 className="aiam-name">
            {selectedAssistant ? selectedAssistant.name : t('aiAssistant.selectAgent')}
          </h1>
        </div>

        <div className="aiam-header-actions">
          {!isEditing && selectedAssistant && (
            <button
              type="button"
              className="u-btn u-btn--primary"
              data-tour="aiam-edit"
              onClick={handleStartEditing}
            >
              <FaPen /> {t('aiAssistant.edit')}
            </button>
          )}
          {isEditing && (
            <>
              <button type="button" className="u-btn u-btn--primary" data-tour="aiam-save" onClick={handleUpdateAIAssistant}>
                {t('aiAssistant.save')}
              </button>
              <button type="button" className="u-btn u-btn--secondary" onClick={handleCancelEditing}>
                {t('aiAssistant.cancel')}
              </button>
            </>
          )}
        </div>
      </div>

      {selectedAssistant && (
        <div className="aiam-text-tab">
          {/* Tools strip — capabilities on top, full width */}
          <div className="aiam-tools-strip">
            <span className="aiam-tools-strip-label">
              <FaTools aria-hidden="true" /> {t('aiAssistant.tools')}
            </span>
            <div className="aiam-tools-strip-chips">
              {availableTools
                .filter(tool => selectedToolIds.includes(tool.id))
                .map((tool) => (
                  <span key={tool.id} className={`agent-tool-chip effect-${tool.effect || 'unknown'}`} title={[tool.description || tool.name, tool.effect, tool.policy].filter(Boolean).join(' · ')}>
                    {getToolTranslation(tool.name)}
                  </span>
                ))}
              {availableTools.filter(tool => selectedToolIds.includes(tool.id)).length === 0 && (
                <span className="aiam-tools-strip-empty">
                  {t('aiAssistant.noToolsSelected') || 'Aucun outil sélectionné'}
                </span>
              )}
            </div>
            {isEditing && (
              <button
                type="button"
                className="u-btn u-btn--secondary aiam-tools-strip-add"
                onClick={() => setShowToolsOverlay(true)}
              >
                <FaPlus /> {t('aiAssistant.addTools')}
              </button>
            )}
          </div>

          {/* Instructions document — the hero content */}
          <div className="aiam-prompt-doc">
            <div className="aiam-prompt-doc-header">
              {isEditing ? (
                <div className="aiam-prompt-doc-namefield">
                  <label htmlFor="name">{t('aiAssistant.name')}</label>
                  <input
                    type="text"
                    id="name"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    className="ai-agent-input"
                  />
                </div>
              ) : (
                <>
                  <div className="aiam-prompt-doc-title">
                    <h4>{t('aiAssistant.prompt')}</h4>
                    {promptWordCount > 0 && (
                      <span className="aiam-prompt-doc-count">
                        {t('aiAssistant.promptWordCount', '{{total}} mots', { total: promptWordCount })}
                      </span>
                    )}
                  </div>
                  {promptSections.length > 1 && (
                    <nav className="aiam-prompt-doc-nav" aria-label={t('aiAssistant.promptSections', 'Sections')}>
                      {promptSections.map((section) => (
                        <button
                          key={section.num}
                          type="button"
                          className="aiam-prompt-doc-nav-chip"
                          onClick={() => scrollToPromptSection(section.num)}
                        >
                          {section.num}. {section.text.toLowerCase()}
                        </button>
                      ))}
                    </nav>
                  )}
                  {(prompt || '').length > 0 && (
                    <button
                      type="button"
                      className="u-btn u-btn--secondary aiam-prompt-doc-copy"
                      onClick={handleCopyPrompt}
                    >
                      {promptCopied ? <FaCheck /> : <FaRegCopy />}
                      {promptCopied
                        ? t('aiAssistant.promptCopied', 'Copié')
                        : t('aiAssistant.copyPrompt', 'Copier')}
                    </button>
                  )}
                </>
              )}
            </div>
            {isEditing ? (
              <textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                className="ai-agent-textarea aiam-prompt-doc-textarea"
                placeholder={t('aiAssistant.promptPlaceholder') || 'Enter AI instructions...'}
              />
            ) : (
              <div className="aiam-prompt-doc-body">
                {promptBlocks.length > 0 && (prompt || '').trim() ? (
                  promptBlocks.map(renderPromptBlock)
                ) : (
                  <p className="pdoc-p aiam-prompt-doc-empty">
                    {t('aiAssistant.noPrompt') || 'No instructions set'}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Chat Bubble */}
      <div className="floating-chat-bubble">
        <button 
          className="chat-toggle-button"
          onClick={handleChatBubbleToggle}
          aria-label="Toggle chat"
        >
          {showChatBubble ? <FaTimes /> : <FaCommentDots />}
        </button>
        
        {showChatBubble && (
          <div className="chat-bubble-container">
            <div className="chat-bubble-header">
              <span className="chat-bubble-title">
                {selectedAssistant?.name || 'AI Assistant'}
              </span>
              <div className="chat-bubble-controls">
                <button 
                  className="chat-delete-button"
                  onClick={handleDeleteConversation}
                  aria-label="Delete conversation"
                >
                  <FaTrash />
                </button>
                <button 
                  className="chat-close-button"
                  onClick={handleChatBubbleToggle}
                  aria-label="Close chat"
                >
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="chat-bubble-content">
              <ChatbotTest 
                ref={chatbotTestRef}
                assistantId={selectedAssistant?._id}
                assistantName={selectedAssistant?.name}
              />
            </div>
          </div>
        )}
      </div>

      {/* Agent Selector Modal */}
      {showAgentSelector && (
        <div className="modalai">
          <div className="modal-content agent-selector-modal">
            <h3>{t('aiAssistant.selectOrCreate')}</h3>
            <div className="agent-list">
              {aiAssistants.map((assistant) => (
                <div
                  key={assistant._id}
                  className={`agent-item ${assistant.is_running ? 'running' : ''}`}
                >
                  <div 
                    className="agent-item-content"
                    onClick={() => {
                      handleSelectAssistant(assistant);
                      setShowAgentSelector(false);
                    }}
                  >
                    <div className="agent-circle small">
                      <FaRobot />
                    </div>
                    <div className="agent-item-info">
                      <span className="agent-item-name">{assistant.name}</span>
                    </div>
                  </div>
                  <Button
                    variant={assistant.is_running ? "primary" : "tertiary"}
                    className={`run-button ${assistant.is_running ? 'running' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSetRunning(assistant._id);
                    }}
                  >
                    <FaPlay /> {assistant.is_running ? t('aiAssistant.running') : t('aiAssistant.run')}
                  </Button>
                </div>
              ))}
            </div>
            <div className="modal-actions">
              <Button
                variant="primary"
                onClick={() => {
                  setShowAgentSelector(false);
                  setShowCreateAgentModal(true);
                }}
              >
                <FaPlus /> {t('aiAssistant.createNew')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowAgentSelector(false)}
              >
                {t('aiAssistant.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Create Agent Modal */}
      {showCreateAgentModal && (
        <div className="ai-agent-modal">
          <div className="ai-agent-modal-content">
            <h3>{t('aiAssistant.createAssistant')}</h3>
            <div className="form-group">
              <label htmlFor="name">{t('aiAssistant.name')}:</label>
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="ai-agent-input"
              />
            </div>
            <div className="form-group">
              <label htmlFor="prompt">{t('aiAssistant.prompt')}:</label>
              <textarea
                id="prompt"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows="4"
                className="ai-agent-textarea"
              />
            </div>
            <div className="ai-agent-tools-section">
              <h4>{t('aiAssistant.selectTools')}</h4>
              <div className="ai-agent-tools-grid">
                {availableTools.map((tool) => (
                  <div key={tool.id} className="ai-agent-tool-item">
                    <input
                      type="checkbox"
                      id={`tool-${tool.id}`}
                      checked={selectedToolIds.includes(tool.id)}
                      onChange={(e) => {
                        const toolId = tool.id;
                        if (e.target.checked) {
                          setSelectedToolIds(prev => [...prev, toolId]);
                        } else {
                          setSelectedToolIds(prev => prev.filter(id => id !== toolId));
                        }
                      }}
                    />
                    <label htmlFor={`tool-${tool.id}`}>{getToolTranslation(tool.name)}</label>
                {toolEffectBadge(tool)}
                    {toolPolicySelect(tool)}
                  </div>
                ))}
              </div>
            </div>
            <div className="modal-actions">
              <Button
                variant="primary"
                onClick={() => {
                  handleCreateAIAssistant();
                  setShowCreateAgentModal(false);
                }}
              >
                {t('aiAssistant.create')}
              </Button>
              <Button
                variant="secondary"
                onClick={() => setShowCreateAgentModal(false)}
              >
                {t('aiAssistant.cancel')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {selectedAssistant && !isEditing && (
        <>
          <AssistantVersions
            assistantId={selectedAssistant.assistant_id || selectedAssistant._id}
            currentInstructions={selectedAssistant.prompt}
            onApplied={() => fetchAssistantDetail(selectedAssistant._id, localStorage.getItem('access_token'))}
          />
          <AssistantRules assistantId={selectedAssistant.assistant_id || selectedAssistant._id} />
        </>
      )}

      {showPromptModal && (
        <div className="modalai">
          <div className="modal-content prompt-modal">
            <h3>{t('aiAssistant.prompt')}</h3>
            {isEditing ? (
              <textarea
                value={editedPrompt}
                onChange={(e) => setEditedPrompt(e.target.value)}
                rows="15"
                className="ai-agent-textarea"
              />
            ) : (
              <div className="prompt-preview-content">
                {prompt}
              </div>
            )}
            <div className="modal-actions">
              {isEditing && (
                <Button 
                  variant="primary" 
                  onClick={handleUpdateAIAssistant}
                >
                  {t('aiAssistant.save')}
                </Button>
              )}
              <Button 
                variant="secondary" 
                onClick={handleClosePromptModal}
              >
                {t('aiAssistant.close')}
              </Button>
            </div>
          </div>
        </div>
      )}

      <ToolsOverlay 
        show={showToolsOverlay}
        onClose={() => setShowToolsOverlay(false)}
        availableTools={availableTools}
        selectedToolIds={isEditing ? editedToolIds : selectedToolIds}
        onToolToggle={(toolId, checked) => {
          // Functional updates: the group "all / none" buttons toggle several
          // tools in one event.
          setEditedToolIds((prev) => (checked
            ? (prev.includes(toolId) ? prev : [...prev, toolId])
            : prev.filter((id) => id !== toolId)));
        }}
        query={toolsQuery}
        setQuery={setToolsQuery}
        openGroups={toolsOpenGroups}
        setOpenGroups={setToolsOpenGroups}
      />
    </div>
  );
};

export default AIAssistantManagement;
